import { Box, Card, CircularProgress, IconButton, Link, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { DateTime } from 'luxon';
import { DeviceWithMapInfo, RentalWithTrailer } from 'src/models';
import useModelData from 'src/hooks/use-model-data';
import Iconify from 'src/components/iconify';
import AssetPreview from 'src/components/asset-preview';
import TruxSimpleTextField from 'src/components/inputs/trux-simple-text-field';
import { GOOGLE_MAPS_API_KEY } from 'src/config-global';
import {
  GoogleMapWrapper,
  MarkerClustererComponent,
  MarkerData,
} from 'src/pages/app/devices/map-google';
import { formatDatetimeForPreview } from 'src/utils/date-utils';
import { useRentalsCustomerAuth } from 'src/auth/rentals-customer/rentals-customer-auth';
import rentalsCustomerAxios, { rentalsCustomerEndpoints } from 'src/utils/rentals-customer-axios';
import { Constants, isRentalTruckType } from 'src/constants';
import { DocumentButton, rentalAgreementUrl } from './trailers';

type PageModel = {
  rentalData: {
    rentals: RentalWithTrailer[];
  };
};

function rentalPosition(rental: RentalWithTrailer) {
  if (!rental.device || !Number(rental.device.lastPosLat) || !Number(rental.device.lastPosLong)) {
    return null;
  }
  return { lat: Number(rental.device.lastPosLat), lng: Number(rental.device.lastPosLong) };
}

function TrailerBlock({
  rental,
  timezone,
  onClick,
}: {
  rental: RentalWithTrailer;
  timezone: string;
  onClick: VoidFunction;
}) {
  const position = rentalPosition(rental);

  return (
    <Box sx={{ px: 1, py: 0.25 }}>
      <Card
        sx={{
          p: 1.5,
          display: 'flex',
          border: '1px solid #E9ECEF',
          borderRadius: '8px',
          justifyContent: 'space-between',
          flexDirection: 'column',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': position
            ? {
                cursor: 'pointer',
                backgroundColor: '#F8F9FA',
                borderColor: '#D1D5DB',
                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                transform: 'translateY(-1px)',
              }
            : undefined,
          position: 'relative',
          mb: 0.5,
        }}
        onClick={() => {
          if (position) {
            onClick();
          }
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontSize: '13px', fontWeight: 600, color: '#1D2939', lineHeight: 1.2, mb: 0.25 }}
        >
          {isRentalTruckType(rental.event.trailerType) ? 'Truck' : 'Trailer'} {rental.event.trailerUnitNumber}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: '10px' }}>
          {rental.device?.lastPing ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '10px',
                  color: '#6B7280',
                  lineHeight: 1.1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {rental.device.location || 'Location unavailable'}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '10px', color: '#6B7280', lineHeight: 1.1 }}>
                {formatDatetimeForPreview(rental.device.lastPing, timezone)}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ fontSize: '10px', color: '#6B7280', lineHeight: 1.1 }}>
              No GPS signal yet
            </Typography>
          )}
        </Box>
      </Card>
    </Box>
  );
}

function TrailerInfo({
  rental,
  timezone,
  onClose,
}: {
  rental: RentalWithTrailer;
  timezone: string;
  onClose: VoidFunction;
}) {
  const {device} = rental;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          minHeight: '48px',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
          Rental Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Iconify icon="solar:arrow-left-linear" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <Box sx={{ textAlign: 'center', p: { xs: 1, md: 1.5 }, borderBottom: '1px solid #e0e0e0' }}>
          {device && <AssetPreview device={device} />}

          <Typography
            variant="h6"
            sx={{ fontWeight: 'bold', mb: 0.5, fontSize: { xs: '14px', md: '16px' } }}
          >
            {device?.deviceType ||
              `${isRentalTruckType(rental.event.trailerType) ? 'Truck' : 'Trailer'} ${rental.event.trailerUnitNumber}`}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 0.5, fontSize: { xs: '10px', md: '11px' }, lineHeight: 1.3 }}
          >
            <strong>Trailer:</strong> {rental.event.trailerUnitNumber}
            {rental.event.make && rental.event.model && (
              <>
                <br />
                <span>
                  {rental.event.make} {rental.event.model}
                </span>
              </>
            )}
            {rental.event.year && <span> ({rental.event.year})</span>}
          </Typography>

          {rental.trailer?.licensePlate && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.5, fontSize: { xs: '10px', md: '11px' } }}
            >
              <strong>License:</strong> {rental.trailer.licensePlate}
              {rental.trailer.licensePlateState && <span> ({rental.trailer.licensePlateState})</span>}
            </Typography>
          )}
        </Box>

        <Box sx={{ borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ p: { xs: 1, md: 1.5 }, pb: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, md: 1 },
                fontSize: { xs: '13px', md: '14px' },
                fontWeight: '600',
              }}
            >
              <Iconify icon="mdi:clipboard-text" width={16} height={16} />
              Details
            </Typography>
          </Box>
          <Box sx={{ p: 1 }}>
            <Stack spacing={0}>
              <Box sx={{ py: 0.5, px: 1, borderBottom: '1px solid #f0f0f0' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px' }}>
                  Rented Since
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                  {DateTime.fromISO(rental.event.eventTime).toFormat('MM/dd/yyyy')}
                </Typography>
              </Box>

              <Box sx={{ py: 0.5, px: 1, borderBottom: '1px solid #f0f0f0' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px' }}>
                  Current Location
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px' }}>
                  {device?.location || 'Unknown'}
                </Typography>
              </Box>

              {device && (
                <Box sx={{ py: 0.5, px: 1, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px' }}>
                    Device Coordinates
                  </Typography>
                  <Link
                    href={`https://maps.google.com/?q=${device.lastPosLat},${device.lastPosLong}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      textDecoration: 'none',
                      color: 'blue',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {device.lastPosLat}, {device.lastPosLong}
                  </Link>
                </Box>
              )}

              <Box sx={{ py: 0.5, px: 1, borderBottom: '1px solid #f0f0f0' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px' }}>
                  Last Ping
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px' }}>
                  {device?.lastPing
                    ? formatDatetimeForPreview(device.lastPing, timezone)
                    : 'No GPS signal yet'}
                </Typography>
              </Box>

              {(device?.battery || 0) > 0 && (
                <Box sx={{ py: 0.5, px: 1, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px' }}>
                    Battery
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 'bold', fontSize: '12px', color: '#4CAF50' }}
                  >
                    {device!.battery}%
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </Box>

        <Box sx={{ borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ p: { xs: 1, md: 1.5 }, pb: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, md: 1 },
                fontSize: { xs: '13px', md: '14px' },
                fontWeight: '600',
              }}
            >
              <Iconify icon="solar:document-text-bold" width={16} height={16} />
              Documents
            </Typography>
          </Box>
          <Box sx={{ p: 1, pt: 0.5 }}>
            {rentalAgreementUrl(rental) ||
            rental.trailer?.registrationFile ||
            rental.trailer?.insuranceFile ||
            rental.trailer?.inspectionFile ? (
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ px: 1, pb: 0.5 }}>
                <DocumentButton label="Rental Agreement" url={rentalAgreementUrl(rental)} />
                <DocumentButton label="Registration" url={rental.trailer?.registrationFile || ''} />
                <DocumentButton label="Insurance" url={rental.trailer?.insuranceFile || ''} />
                <DocumentButton label="Inspection" url={rental.trailer?.inspectionFile || ''} />
              </Stack>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 1, pb: 0.5, fontSize: '11px' }}
              >
                No documents yet
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function TrailerSidebar({
  rentals,
  timezone,
  searchText,
  setSearchText,
  collapsed,
  setCollapsed,
  mapRef,
  setOpenInfoWindowUuid,
  selectedRental,
  setSelectedRental,
}: {
  rentals: RentalWithTrailer[];
  timezone: string;
  searchText: string;
  setSearchText: (value: string) => void;
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  mapRef: React.MutableRefObject<google.maps.Map | null>;
  setOpenInfoWindowUuid: (uuid: string | null) => void;
  selectedRental: RentalWithTrailer | undefined;
  setSelectedRental: (rental: RentalWithTrailer | undefined) => void;
}) {
  const filteredRentals = rentals.filter((rental) => {
    const haystack = [rental.event.trailerUnitNumber, rental.event.make, rental.event.model]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(searchText.toLowerCase());
  });

  return (
    <Box
      sx={{
        borderLeft: '1px solid #EFEFEF',
        borderTop: '1px solid #EFEFEF',
        position: { md: 'relative', xs: 'absolute' },
        top: { xs: '0px', md: null },
        bottom: { xs: '0px', md: null },
        left: { xs: '0px', md: null },
        right: collapsed ? undefined : { xs: '40px', md: null },
        zIndex: 1200,
      }}
    >
      <Box
        sx={{
          background: '#ffffff',
          boxShadow: '5px 0 7px -7px rgba(0, 0, 0, 0.5)',
          zIndex: 0,
          overflowY: 'auto',
          overscrollBehavior: 'none',
          height: '100%',
          width: collapsed ? '0px' : { xs: 'calc(100% - 30px)', md: '300px' },
        }}
        display="flex"
        flexDirection="column"
      >
        {selectedRental ? (
          <TrailerInfo
            rental={selectedRental}
            timezone={timezone}
            onClose={() => {
              setSelectedRental(undefined);
              setOpenInfoWindowUuid(null);
            }}
          />
        ) : (
          <>
            <Box p={1} display="flex" gap={1} sx={{ borderBottom: '1px solid #EEE' }}>
              <Box flex={1}>
                <TruxSimpleTextField
                  value={searchText}
                  onChange={setSearchText}
                  label="Search Rentals"
                />
              </Box>
            </Box>

            <Box flexGrow={1} sx={{ display: 'flex', flexDirection: 'column' }}>
              {filteredRentals.length === 0 && (
                <Box pl={1}>
                  <Typography component="div" padding={2} color="grey">
                    <Box display="flex" gap={1}>
                      <Iconify icon="octicon:alert-16" />
                      No Rentals Found
                    </Box>
                  </Typography>
                </Box>
              )}
              <Box sx={{ pt: 0.5 }}>
                {filteredRentals.map((rental) => (
                  <TrailerBlock
                    key={rental.event.id}
                    rental={rental}
                    timezone={timezone}
                    onClick={() => {
                      setSelectedRental(rental);
                      const position = rentalPosition(rental);
                      if (mapRef.current && position) {
                        mapRef.current.panTo(position);
                        if ((mapRef.current.getZoom() || 100) < 12) {
                          mapRef.current.setZoom(12);
                        }
                        setTimeout(() => {
                          setOpenInfoWindowUuid(rental.device!.deviceUuid);
                        }, 51);
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </>
        )}
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: { xs: collapsed ? 'calc(50% - 40px)' : '0px', md: 'calc(50% - 25px)' },
          bottom: { xs: collapsed ? undefined : '0px', md: 'initial' },
          borderLeft: { xs: collapsed ? 'none' : '1px solid #DDD', md: 'none' },
          display: 'flex',
          alignItems: 'center',
          right: { xs: '-41px', md: '-25px' },
          zIndex: 1000,
          padding: { xs: '10px', md: '2px' },
          paddingTop: { xs: '40px', md: '20px' },
          paddingBottom: { xs: '40px', md: '20px' },
          borderTopRightRadius: '4px',
          borderBottomRightRadius: '4px',
          background: '#fafafa',
          border: '1px solid #e5e6e6',
          boxShadow: { xs: '4px 3px 5px #AAAAAA55', md: 'none' },
        }}
        onClick={() => setCollapsed(!collapsed)}
      >
        {collapsed ? (
          <Iconify icon="line-md:chevron-right" />
        ) : (
          <Iconify icon="line-md:chevron-left" />
        )}
      </Box>
    </Box>
  );
}

function TrailerMap({
  rentals,
  mapRef,
  timezone,
  openInfoWindowUuid,
  setOpenInfoWindowUuid,
  setSelectedRental,
}: {
  rentals: RentalWithTrailer[];
  mapRef: React.MutableRefObject<google.maps.Map | null>;
  timezone: string;
  openInfoWindowUuid: string | null;
  setOpenInfoWindowUuid: (uuid: string | null) => void;
  setSelectedRental: (rental: RentalWithTrailer | undefined) => void;
}) {
  const positioned = rentals.filter((rental) => rentalPosition(rental));

  const markersData: MarkerData[] = useMemo(
    () =>
      positioned.map((rental) => ({
        device: rental.device!,
        position: rentalPosition(rental)!,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rentals]
  );

  useEffect(() => {
    if (!mapRef.current || !positioned.length) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    positioned.forEach((rental) => {
      bounds.extend(rentalPosition(rental)!);
    });
    mapRef.current.fitBounds(bounds, 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positioned.length]);

  return (
    <GoogleMapWrapper mapType="default" mapRef={mapRef}>
      <MarkerClustererComponent
        deviceEldDrivers={[]}
        markersData={markersData}
        openInfoWindowUuid={openInfoWindowUuid}
        setOpenInfoWindowUuid={setOpenInfoWindowUuid}
        timezone={timezone}
        eldConnectedVehicleUnitNumbers={[]}
        onTruckMarkerClick={(device: DeviceWithMapInfo) => {
          setSelectedRental(rentals.find((rental) => rental.device?.deviceUuid === device.deviceUuid));
        }}
      />
    </GoogleMapWrapper>
  );
}

function PageContent() {
  const { session } = useRentalsCustomerAuth();
  const timezone = session?.organizationSettings?.companyTimeZone || 'America/Chicago';

  const dataLoader = useCallback(
    () => ({
      rentalData: rentalsCustomerAxios
        .get(rentalsCustomerEndpoints.rentalData)
        .then((response) => response.data),
    }),
    []
  );

  const { data, error, loading } = useModelData<PageModel>(dataLoader);

  // the map shows only confirmed rentals — until staff confirms, the backend
  // withholds the GPS device and the trailer stays off the map entirely
  const confirmedRentals = (data?.rentalData.rentals || []).filter(
    (rental) =>
      rental.event.rentalTrailerEventStatus === Constants.RENTAL_TRAILER_EVENT_STATUS_SIGNED
  );

  const mapRef = useRef<google.maps.Map | null>(null);
  const [searchText, setSearchText] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [openInfoWindowUuid, setOpenInfoWindowUuid] = useState<string | null>(null);
  const [selectedRental, setSelectedRental] = useState<RentalWithTrailer | undefined>(undefined);

  return (
    <Box display="flex" flexDirection="row" height="100%" marginTop="-15px" position="relative">
      {loading && !data && (
        <Box component="div" sx={{ marginTop: '20vh', textAlign: 'center', margin: 'auto' }}>
          <CircularProgress size={50} />
        </Box>
      )}

      {!loading && error && (
        <Card sx={{ m: 4, width: '100%', p: 4, textAlign: 'center' }}>
          <Typography component="div" padding={2} color="error">
            <Box display="flex" gap={1} justifyContent="center">
              <Iconify icon="octicon:alert-16" />{' '}
              <i>Error loading the data {error?.message ?? String(error)}</i>
            </Box>
          </Typography>
        </Card>
      )}

      {!loading && data && (
        <Box
          position="absolute"
          left="0px"
          top="0px"
          right="0px"
          bottom="-15px"
          display="flex"
          flexDirection="row"
        >
          <TrailerSidebar
            rentals={confirmedRentals}
            timezone={timezone}
            searchText={searchText}
            setSearchText={setSearchText}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            mapRef={mapRef}
            setOpenInfoWindowUuid={setOpenInfoWindowUuid}
            selectedRental={selectedRental}
            setSelectedRental={setSelectedRental}
          />
          <Box flexGrow={1} position="relative">
            <TrailerMap
              rentals={confirmedRentals}
              mapRef={mapRef}
              timezone={timezone}
              openInfoWindowUuid={openInfoWindowUuid}
              setOpenInfoWindowUuid={setOpenInfoWindowUuid}
              setSelectedRental={setSelectedRental}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function Page() {
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <PageContent />
    </APIProvider>
  );
}
